import styled from '@emotion/styled';
import { Button, useTheme } from '@mui/material';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ReactComponent as ReviewIcon } from '../../../assets/icons/search_review.svg';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { Typography } from '../../../components/Typography/Typography';
import { ROUTES } from '../../../constants/routes';
import { Timesheet, TimesheetStatusLabel } from '../../../types';
import {
  getCalendarYearMonthFromTimesheetWeekStart,
  pendingMonthTimesheetDomId,
} from '../../../utils/pendingTimesheetsMonthWeeks';
import DealCell from '../components/DealsCell';
import { LastEditedCell } from '../../../components/Table/CellRenderers/LastEditedCell';
import { StatusCell } from '../components/StatusCell';
import WeekCell from '../components/WeekCell';
import { useAtomValue } from 'jotai';
import { isSmallScreenState, userState } from '../../../state/UIState';

const CellWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const columnHelper = createColumnHelper<Timesheet>();

export const ActionCell = (props: CellContext<Timesheet, unknown>) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const issuedTimesheet = props.row.original;

  return (
    <CellWrapper style={{ justifyContent: 'center', padding: '0px' }}>
      <Button
        onClick={() => {
          const ym = getCalendarYearMonthFromTimesheetWeekStart(issuedTimesheet);
          if (ym) {
            const hash = pendingMonthTimesheetDomId(issuedTimesheet.id);
            navigate(
              `/${ROUTES.PENDING_TIMESHEETS}/month/${ym.year}/${ym.month}#${hash}`
            );
          } else {
            navigate(`/${ROUTES.PENDING_TIMESHEETS}/${issuedTimesheet.id}`);
          }
        }}
        variant='text'
        style={{
          height: '28px',
          backgroundColor: colors.surfaceBackground.bg1,
          border: `1px solid ${colors.border.default}`,
        }}
        startIcon={<ReviewIcon />}
        sx={{
          '.MuiButton-startIcon': { marginRight: '2px' },
          '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
          svg: { g: { path: { fill: colors.icon.accent } } },
        }}
        disabled={issuedTimesheet.id === 0}
      >
        <Typography variant='h4' color={colors.textAccent.default} style={{ marginBottom: '-2px' }}>
          Review
        </Typography>
      </Button>
    </CellWrapper>
  );
};

export const usePendingTimesheetsTableColumns = () => {
  const isSmallScreen = useAtomValue(isSmallScreenState);
  // This is the Submitter's OWN list, so the owner is always the logged-in user. The own-list API
  // query does not load the `user` relation, so we supply the owner id here to keep the "by <editor>"
  // attribution working when a Proxy edited the submitter's timesheet.
  const user = useAtomValue(userState);
  const columns = useMemo(
    () => [
      columnHelper.accessor('weekStart', {
        cell: (info) => <WeekCell info={info} />,
        header: () => <TableHeaderCell text='Week' />,
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const filterValueMonth = Number(filterValue.slice(5, 7));
          const cellValueMonth = Number(row.original.weekDates[0]?.split('-')[1]);

          const filterValueYear = Number(filterValue.slice(0, 4));
          const cellValueYear = Number(row.original.weekDates[0]?.split('-')[0]);

          const isDateMatched =
            filterValueMonth === cellValueMonth && filterValueYear === cellValueYear;
          return isDateMatched;
        },
        meta: {
          width: isSmallScreen ? '150px' : '400px',
          minWidth: isSmallScreen ? '150px' : '400px',
          maxWidth: isSmallScreen ? '150px' : '400px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { padding: '0' },
        },
      }),
      columnHelper.accessor('status', {
        cell: (info) => <StatusCell status={info.getValue() as TimesheetStatusLabel} />,
        header: () => <TableHeaderCell text='TS Status' />,
        meta: {
          width: '150px',
          minWidth: '150px',
          maxWidth: '150px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.accessor('timesheetRows', {
        cell: (info) => <DealCell deals={info.row.original.timesheetRows} />,
        header: () => <TableHeaderCell text='Deals' />,
        enableSorting: false,

        meta: {
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const deals = row.original.timesheetRows;

          const hasMatch = deals.some((deal) =>
            deal.name.toLocaleLowerCase().includes(filterValue.toLowerCase())
          );
          return hasMatch;
        },
      }),
      columnHelper.accessor('updated_at', {
        cell: (info) => (
          <LastEditedCell
            updated_at={info.row.original.updated_at}
            updatedBy={info.row.original.updatedBy}
            ownerId={user?.id}
          />
        ),
        header: () => <TableHeaderCell text='Last Edited' />,
        meta: {
          width: isSmallScreen ? '150px' : '300px',
          minWidth: isSmallScreen ? '150px' : '300px',
          maxWidth: isSmallScreen ? '150px' : '300px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => <ActionCell {...info} />,
        header: () => <TableHeaderCell text='Action' />,
        meta: {
          width: '130px',
          minWidth: '130px',
          maxWidth: '130px',
          tdStyles: {
            paddingLeft: '16px',
            borderLeft: `none`,
          },
          thStyles: { borderLeft: `none` },
        },
      }),
    ],
    [isSmallScreen, user?.id]
  );

  return columns;
};
